/**
 * Shared types for Slack assignment fields.
 * Used for dynamic field fetching from assignment-related entities in Slack messages.
 */

/**
 * Allowed field names for User objects (assignee and upNext) in message body.
 * These are standard Salesforce User fields that can be displayed in the message.
 */
export const ALLOWED_USER_FIELDS = ['Id', 'Email', 'Name', 'FirstName'] as const;

/**
 * Allowed field names for User objects when used as recipients.
 * Only Email is allowed because Slack users are looked up by email address.
 * There is no SF User ID → Slack User ID mapping.
 */
export const ALLOWED_USER_RECIPIENT_FIELDS = ['Email'] as const;

/**
 * Allowed field names for Assignment Group objects.
 * These are the fields available on the custom Assignment Group object.
 */
export const ALLOWED_ASSIGNMENT_GROUP_FIELDS = ['Id', 'Name'] as const;

export type AllowedUserField = typeof ALLOWED_USER_FIELDS[number];
export type AllowedUserRecipientField = typeof ALLOWED_USER_RECIPIENT_FIELDS[number];
export type AllowedAssignmentGroupField = typeof ALLOWED_ASSIGNMENT_GROUP_FIELDS[number];

/**
 * Wrapper characters for assignment-related dynamic fields in message templates.
 * Fields wrapped with these characters are resolved from assignment context at runtime.
 */
export const ASSIGNMENT_FIELD_WRAPPER = '^^^';

/**
 * Keys for field names from assignment.
 * These represent the different sources of fields in an assignment context.
 */
export enum FieldNamesFromAssignmentKey {
  /** Fields from the assigned user (current assignee) */
  ASSIGNEE = 'assignee',
  /** Fields from the next person in round-robin queue */
  UP_NEXT = 'upNext',
  /** Fields from the assignment group */
  ASSIGNMENT_GROUP = 'assignmentGroup',
}

/**
 * Structure for specifying which fields to fetch from assignment-related entities.
 * Used in Slack message actions within assignment rules to enable dynamic field queries.
 *
 * @example
 * ```typescript
 * const fieldNamesFromAssignment: FieldNamesFromAssignment = {
 *   assignee: ['Id', 'Email', 'Name'],
 *   upNext: ['Id', 'Email'],
 *   assignmentGroup: ['Id', 'Name'],
 * };
 * ```
 */
export interface FieldNamesFromAssignment {
  /** Fields to fetch from the assigned user (e.g., Id, Email, Name, FirstName) */
  [FieldNamesFromAssignmentKey.ASSIGNEE]?: string[];
  /** Fields to fetch from the next person in round-robin (e.g., Id, Email, Name, FirstName) */
  [FieldNamesFromAssignmentKey.UP_NEXT]?: string[];
  /** Fields to fetch from the assignment group (e.g., Id, Name) */
  [FieldNamesFromAssignmentKey.ASSIGNMENT_GROUP]?: string[];
}

/**
 * Checks if a FieldNamesFromAssignment object has any valid field names.
 * Returns false if undefined, null, or all arrays are empty/undefined.
 */
export function hasFieldNamesFromAssignment(
  fieldNames: FieldNamesFromAssignment | undefined | null,
): boolean {
  if (!fieldNames) {
    return false;
  }

  const { assignee, upNext, assignmentGroup } = fieldNames;

  return (
    (Array.isArray(assignee) && assignee.length > 0) ||
    (Array.isArray(upNext) && upNext.length > 0) ||
    (Array.isArray(assignmentGroup) && assignmentGroup.length > 0)
  );
}

/**
 * Normalizes a FieldNamesFromAssignment object by removing empty arrays.
 * Returns undefined if the result would be an empty object.
 */
export function normalizeFieldNamesFromAssignment(
  fieldNames: FieldNamesFromAssignment | undefined | null,
): FieldNamesFromAssignment | undefined {
  if (!hasFieldNamesFromAssignment(fieldNames)) {
    return undefined;
  }

  const result: FieldNamesFromAssignment = {};

  if (fieldNames?.assignee && fieldNames.assignee.length > 0) {
    result.assignee = fieldNames.assignee;
  }
  if (fieldNames?.upNext && fieldNames.upNext.length > 0) {
    result.upNext = fieldNames.upNext;
  }
  if (fieldNames?.assignmentGroup && fieldNames.assignmentGroup.length > 0) {
    result.assignmentGroup = fieldNames.assignmentGroup;
  }

  return result;
}

/**
 * Wraps an assignment field reference with the assignment field wrapper.
 * Used in message templates to denote fields that should be resolved from assignment context.
 *
 * @example
 * wrapAssignmentField(FieldNamesFromAssignmentKey.ASSIGNEE, 'Email')
 * // Returns: '^^^assignee.Email^^^'
 */
export function wrapAssignmentField(key: FieldNamesFromAssignmentKey, fieldName: string): string {
  return `${ASSIGNMENT_FIELD_WRAPPER}${key}.${fieldName}${ASSIGNMENT_FIELD_WRAPPER}`;
}

/**
 * Checks if a string is a wrapped assignment field reference.
 */
export function isWrappedAssignmentField(value: string): boolean {
  return value.startsWith(ASSIGNMENT_FIELD_WRAPPER) && value.endsWith(ASSIGNMENT_FIELD_WRAPPER);
}

/**
 * Parses a wrapped assignment field reference.
 * Returns the key and field name, or null if invalid.
 *
 * @example
 * parseAssignmentFieldRef('^^^assignee.Email^^^')
 * // Returns: { key: 'assignee', fieldName: 'Email' }
 */
export function parseAssignmentFieldRef(
  wrappedValue: string,
): { key: FieldNamesFromAssignmentKey; fieldName: string } | null {
  if (!isWrappedAssignmentField(wrappedValue)) {
    return null;
  }

  const inner = wrappedValue.slice(
    ASSIGNMENT_FIELD_WRAPPER.length,
    -ASSIGNMENT_FIELD_WRAPPER.length,
  );
  const [key, fieldName] = inner.split('.');

  if (
    !key ||
    !fieldName ||
    !Object.values(FieldNamesFromAssignmentKey).includes(key as FieldNamesFromAssignmentKey)
  ) {
    return null;
  }

  return { key: key as FieldNamesFromAssignmentKey, fieldName };
}
